import React, { useEffect, useMemo, useState } from 'react';
import { DndContext, DragOverlay, KeyboardSensor, PointerSensor, TouchSensor, closestCenter, pointerWithin, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Plus, FolderPlus, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../lib/api';
import { useLang, tx } from '../../../i18n/LanguageContext';
import { LOOSE, kind, raw, buildItems } from './meta';
import { SectionBlock } from './SectionBlock';
import { LessonRow, LessonRowView } from './LessonRow';
import { LessonDialog } from './LessonDialog';
import { ConfirmDialog } from './ConfirmDialog';
import { useDurationBackfill } from './useDurationBackfill';

const collision = (args) => {
  const isSection = kind(args.active.id) === 's';
  const pool = args.droppableContainers.filter(c => (kind(c.id) === 's') === isSection);
  if (isSection) return closestCenter({ ...args, droppableContainers: pool });
  const hits = pointerWithin({ ...args, droppableContainers: pool });
  const lessonHits = hits.filter(h => kind(h.id) === 'l');
  if (lessonHits.length) return closestCenter({ ...args, droppableContainers: pool.filter(c => lessonHits.some(h => h.id === c.id)) });
  return hits.length ? hits : closestCenter({ ...args, droppableContainers: pool });
};

export const ContentManager = ({ courseId, sections, lessons, setSections, setLessons }) => {
  const { lang } = useLang();
  const L = (ar, en) => tx(lang, ar, en);
  const sorted = useMemo(() => [...sections].sort((a, b) => (a.order || 0) - (b.order || 0)), [sections]);
  const serverItems = useMemo(() => buildItems(sorted, lessons), [sorted, lessons]);
  const [order, setOrder] = useState(() => sorted.map(s => s.id));
  const [items, setItems] = useState(serverItems);
  const [activeId, setActiveId] = useState(null);
  const [keepLoose, setKeepLoose] = useState(false);
  const [closed, setClosed] = useState(() => new Set());
  const [dialog, setDialog] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [draft, setDraft] = useState(null);
  const byId = useMemo(() => Object.fromEntries(lessons.map(l => [l.id, l])), [lessons]);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => { if (!activeId) { setOrder(sorted.map(s => s.id)); setItems(serverItems); } }, [sorted, serverItems]); // eslint-disable-line react-hooks/exhaustive-deps
  useDurationBackfill(courseId, lessons, setLessons);

  const findC = (id) => Object.keys(items).find(k => items[k].includes(id));
  const toggle = (id) => setClosed(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const persist = async (nextOrder, nextItems) => {
    const payload = { sections: nextOrder, lessons: [LOOSE, ...nextOrder].flatMap(c => (nextItems[c] || []).map(id => ({ id, section_id: c === LOOSE ? '' : c }))) };
    try {
      const { data } = await api.put(`/courses/${courseId}/structure`, payload);
      setSections(data.sections); setLessons(data.lessons);
      toast.success(L('تم حفظ الترتيب', 'Order saved'));
    } catch (e) {
      setOrder(sorted.map(s => s.id)); setItems(serverItems);
      toast.error(L('تعذر حفظ الترتيب', 'Could not save the order'));
    }
  };

  const onDragOver = ({ active, over }) => {
    if (kind(active.id) !== 'l' || !over) return;
    const a = raw(active.id);
    const from = findC(a);
    const to = kind(over.id) === 'd' ? raw(over.id) : findC(raw(over.id));
    if (!from || !to || from === to) return;
    setItems(prev => {
      const dst = prev[to].filter(x => x !== a);
      const idx = kind(over.id) === 'l' ? dst.indexOf(raw(over.id)) : dst.length;
      dst.splice(idx < 0 ? dst.length : idx, 0, a);
      return { ...prev, [from]: prev[from].filter(x => x !== a), [to]: dst };
    });
    setClosed(s => { const n = new Set(s); n.delete(to); return n; });
  };

  const onDragEnd = ({ active, over }) => {
    setActiveId(null);
    if (kind(active.id) === 's') {
      if (!over) return;
      const ai = order.indexOf(raw(active.id)), oi = order.indexOf(raw(over.id));
      if (ai < 0 || oi < 0 || ai === oi) return;
      const next = arrayMove(order, ai, oi);
      setOrder(next); persist(next, items);
      return;
    }
    let next = items;
    if (over && kind(over.id) === 'l') {
      const a = raw(active.id), o = raw(over.id), c = findC(a);
      if (c && c === findC(o)) {
        const ai = items[c].indexOf(a), oi = items[c].indexOf(o);
        if (ai !== oi) next = { ...items, [c]: arrayMove(items[c], ai, oi) };
      }
    }
    setItems(next);
    if (JSON.stringify(next) !== JSON.stringify(serverItems)) persist(order, next);
  };

  const addSection = async () => {
    if (!draft?.titleAr.trim()) return;
    try {
      const { data } = await api.post(`/courses/${courseId}/sections`, { titleAr: draft.titleAr.trim(), titleEn: draft.titleEn.trim(), order: sorted.length + 1 });
      setSections([...sections, data]); setDraft(null);
      toast.success(L('تمت إضافة القسم', 'Section added'));
    } catch (e) { toast.error(e?.response?.data?.detail || L('تعذر إضافة القسم', 'Could not add the section')); }
  };
  const renameSection = async (s) => {
    try {
      await api.put(`/courses/${courseId}/sections/${s.id}`, { titleAr: s.titleAr.trim(), titleEn: (s.titleEn || '').trim(), order: s.order || 0 });
      setSections(sections.map(x => (x.id === s.id ? { ...x, titleAr: s.titleAr.trim(), titleEn: (s.titleEn || '').trim() } : x)));
      toast.success(L('تم تعديل اسم القسم', 'Section renamed'));
    } catch (e) { toast.error(L('تعذر الحفظ', 'Could not save')); }
  };
  const doDelete = async () => {
    const { type, item } = confirm;
    try {
      if (type === 'section') {
        await api.delete(`/courses/${courseId}/sections/${item.id}`);
        setSections(sections.filter(x => x.id !== item.id));
        setLessons(lessons.map(l => (l.section_id === item.id ? { ...l, section_id: '' } : l)));
      } else {
        await api.delete(`/courses/${courseId}/lessons/${item.id}`);
        setLessons(lessons.filter(l => l.id !== item.id));
      }
      toast.success(L('تم الحذف', 'Deleted'));
    } catch (e) { toast.error(L('تعذر الحذف', 'Could not delete')); }
    setConfirm(null);
  };
  const toggleFree = async (lesson) => {
    const next = !lesson.is_free;
    const apply = (v) => setLessons(ls => ls.map(l => (l.id === lesson.id ? { ...l, is_free: v } : l)));
    apply(next);
    try {
      const fd = new FormData(); fd.append('is_free', next ? 'true' : 'false');
      await api.patch(`/courses/${courseId}/lessons/${lesson.id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    } catch (e) {
      apply(!next);
      toast.error(L('تعذر الحفظ', 'Could not save'));
    }
  };
  const onSaved = (lesson, isNew) => setLessons(isNew ? [...lessons, lesson] : lessons.map(l => (l.id === lesson.id ? lesson : l)));

  const rowProps = { onEdit: (l) => setDialog({ lesson: l, sectionId: l.section_id }), onDelete: (l) => setConfirm({ type: 'lesson', item: l }), onToggleFree: toggleFree };
  const groups = [...(items[LOOSE]?.length || (activeId && keepLoose) ? [{ id: LOOSE, loose: true }] : []), ...order.map(id => sorted.find(s => s.id === id)).filter(Boolean)];
  const active = activeId && kind(activeId) === 'l' ? byId[raw(activeId)] : null;
  const activeSection = activeId && kind(activeId) === 's' ? sorted.find(s => s.id === raw(activeId)) : null;

  return (
    <div data-testid="content-manager">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="text-base sm:text-lg font-bold text-slate-900">{L('المحتوى والدروس', 'Content & lessons')}</h3>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5">{L('نظّم أقسام الكورس ودروسه من مكان واحد', 'Organise sections and lessons in one place')}</p>
        </div>
        <button type="button" data-testid="content-add-section-top" onClick={() => setDraft(d => d || { titleAr: '', titleEn: '' })} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors">
          <Plus className="w-4 h-4" />{L('إضافة قسم', 'Add section')}
        </button>
      </div>

      <DndContext sensors={sensors} collisionDetection={collision} onDragStart={({ active: a }) => { setActiveId(a.id); setKeepLoose(!!items[LOOSE]?.length); }} onDragOver={onDragOver} onDragEnd={onDragEnd} onDragCancel={() => { setActiveId(null); setItems(serverItems); setOrder(sorted.map(s => s.id)); }}>
        <SortableContext items={order.map(id => `s:${id}`)} strategy={verticalListSortingStrategy}>
          <div className="space-y-3">
            {groups.map((s, i) => {
              const ids = items[s.id] || [];
              const open = !closed.has(s.id) && !(activeSection);
              return (
                <SectionBlock key={s.id} section={s} loose={s.loose} index={s.loose ? -1 : order.indexOf(s.id)} count={ids.length} open={open}
                  onToggle={() => toggle(s.id)} onRename={renameSection} onDelete={(x) => setConfirm({ type: 'section', item: x })} onAdd={(sid) => setDialog({ lesson: null, sectionId: sid })}>
                  <SortableContext items={ids.map(id => `l:${id}`)} strategy={verticalListSortingStrategy}>
                    {ids.map(id => byId[id] && <LessonRow key={id} lesson={byId[id]} {...rowProps} />)}
                  </SortableContext>
                  {!ids.length && <p data-testid={`content-section-empty-${s.id}`} className="text-xs text-slate-400 text-center py-4 rounded-xl border border-dashed border-slate-200">{L('لا توجد دروس بعد — أضف درساً أو اسحب درساً إلى هنا', 'No lessons yet — add one or drag a lesson here')}</p>}
                </SectionBlock>
              );
            })}
          </div>
        </SortableContext>
        <DragOverlay>
          {active ? <LessonRowView lesson={active} dragging /> : activeSection ? <div className="rounded-2xl bg-white border border-blue-300 shadow-xl px-4 py-3 text-sm font-bold text-slate-900">{activeSection.titleAr}</div> : null}
        </DragOverlay>
      </DndContext>

      {draft ? (
        <div data-testid="content-new-section-form" className="mt-3 flex flex-wrap items-center gap-2 rounded-2xl border border-dashed border-blue-300 bg-blue-50/30 p-3">
          <FolderPlus className="w-5 h-5 text-blue-600 shrink-0" />
          <input autoFocus data-testid="content-new-section-ar" value={draft.titleAr} onChange={e => setDraft({ ...draft, titleAr: e.target.value })} onKeyDown={e => e.key === 'Enter' && addSection()} placeholder={L('اسم القسم بالعربية', 'Section name (Arabic)')}
            className="flex-1 min-w-[160px] h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-blue-500" />
          <input dir="ltr" data-testid="content-new-section-en" value={draft.titleEn} onChange={e => setDraft({ ...draft, titleEn: e.target.value })} onKeyDown={e => e.key === 'Enter' && addSection()} placeholder="Section name (English)"
            className="flex-1 min-w-[160px] h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-blue-500" />
          <button type="button" data-testid="content-new-section-save" onClick={addSection} disabled={!draft.titleAr.trim()} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-semibold"><Check className="w-4 h-4" />{L('حفظ', 'Save')}</button>
          <button type="button" onClick={() => setDraft(null)} className="h-10 w-10 inline-flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>
      ) : (
        <button type="button" data-testid="content-add-section-bottom" onClick={() => setDraft({ titleAr: '', titleEn: '' })} className="mt-3 w-full h-12 rounded-2xl border border-dashed border-slate-300 text-slate-600 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50/30 text-sm font-semibold inline-flex items-center justify-center gap-1.5 transition-colors">
          <Plus className="w-4 h-4" />{L('إضافة قسم جديد', 'Add new section')}
        </button>
      )}

      {dialog && <LessonDialog courseId={courseId} lesson={dialog.lesson} sectionId={dialog.sectionId} sections={sorted} lessons={lessons} onClose={() => setDialog(null)} onSaved={onSaved} setLessons={setLessons} />}
      <ConfirmDialog open={!!confirm} onCancel={() => setConfirm(null)} onConfirm={doDelete}
        title={confirm?.type === 'section' ? L('حذف القسم؟', 'Delete section?') : L('حذف الدرس؟', 'Delete lesson?')}
        desc={confirm?.type === 'section' ? L(`سيتم حذف «${confirm.item.titleAr}». دروسه لن تُحذف وستنتقل إلى «دروس بدون قسم».`, 'Its lessons will not be deleted; they move to “Lessons without a section”.') : L(`سيتم حذف «${confirm?.item?.titleAr || ''}» نهائياً.`, 'This lesson will be permanently deleted.')} />
    </div>
  );
};
